import { useEffect, useState } from "react";
import {
    Users, Copy, CheckCheck, Trash2, Plus, X, BarChart3, Flame, FileDown,
    Droplet, AlertTriangle,
} from "lucide-react";
import {
    createClass, fetchMyClasses, fetchClassStats, deleteClass, classCsvUrl,
    fetchClassWater, classWaterCsvUrl,
} from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import PaywallModal from "@/components/PaywallModal";

export default function ClassesPanel() {
    const { t, lang } = useLang();
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState("");
    const [selected, setSelected] = useState(null);
    const [stats, setStats] = useState(null);
    const [waterStats, setWaterStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [copiedCode, setCopiedCode] = useState(null);
    const [paywall, setPaywall] = useState(null);  // { feature } | null

    const load = async () => {
        setLoading(true);
        try {
            const c = await fetchMyClasses();
            setClasses(c);
            if (c.length && !selected) setSelected(c[0]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Allow Watchlist widget to focus a specific class via a global event.
    useEffect(() => {
        const handler = (e) => {
            const id = e.detail;
            if (!id) return;
            const found = classes.find((c) => c.id === id);
            if (found) setSelected(found);
        };
        window.addEventListener("flow:select-class", handler);
        return () => window.removeEventListener("flow:select-class", handler);
    }, [classes]);

    useEffect(() => {
        if (!selected) {
            setStats(null);
            setWaterStats(null);
            return;
        }
        setStatsLoading(true);
        Promise.all([
            fetchClassStats(selected.id),
            fetchClassWater(selected.id).catch(() => null),
        ])
            .then(([s, w]) => {
                setStats(s);
                setWaterStats(w);
            })
            .finally(() => setStatsLoading(false));
    }, [selected]);

    const submitCreate = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        try {
            const c = await createClass(newName.trim());
            setNewName("");
            setCreating(false);
            await load();
            setSelected(c);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            if (err?.response?.status === 402 && detail?.code === "paywall_class_limit") {
                setPaywall({ feature: lang === "es" ? "Crear clases adicionales" : "Creating additional classes" });
            } else {
                window.alert(detail?.message || detail || err.message);
            }
        }
    };

    const remove = async (id) => {
        if (!window.confirm(t.dashboard.confirmDelClass)) return;
        await deleteClass(id);
        const next = classes.filter((c) => c.id !== id);
        setClasses(next);
        setSelected(next[0] || null);
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 1500);
    };

    return (
        <section id="classes-section" className="mt-12" data-testid="classes-section">
            <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
                <h2 className="font-display font-bold text-[var(--cyan)] text-2xl uppercase">
                    {t.dashboard.classes}
                </h2>
                {!creating && (
                    <button
                        onClick={() => setCreating(true)}
                        className="btn-ghost"
                        data-testid="create-class-btn"
                    >
                        <Plus size={14} /> {t.dashboard.createCta}
                    </button>
                )}
            </div>

            {creating && (
                <form
                    onSubmit={submitCreate}
                    className="card p-5 mb-6 flex gap-3 flex-wrap items-end"
                    data-testid="create-class-form"
                >
                    <label className="flex-1 min-w-[200px]">
                        <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)]">
                            {t.dashboard.createClass}
                        </span>
                        <input
                            value={newName}
                            onChange={(e) => setNewName(e.target.value)}
                            placeholder={t.dashboard.classNamePh}
                            className="input-line mt-1"
                            data-testid="create-class-name"
                            required
                            autoFocus
                        />
                    </label>
                    <button
                        type="submit"
                        className="btn-prime"
                        data-testid="create-class-submit"
                    >
                        {t.dashboard.create}
                    </button>
                    <button
                        type="button"
                        onClick={() => {
                            setCreating(false);
                            setNewName("");
                        }}
                        className="btn-ghost"
                    >
                        <X size={14} /> {t.dashboard.cancel}
                    </button>
                </form>
            )}

            {loading ? (
                <p className="font-mono text-[var(--muted)]">…</p>
            ) : classes.length === 0 ? (
                <div className="card p-8 text-center">
                    <p className="font-brush text-[var(--cyan)] text-3xl -rotate-2 inline-block">
                        no classes yet
                    </p>
                    <p className="font-mono text-[var(--muted)] mt-3 text-sm">
                        Spin one up — students join with a 6-char code.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-12 gap-5">
                    <aside className="col-span-12 md:col-span-4 space-y-3" data-testid="classes-list">
                        {classes.map((c) => (
                            <div
                                key={c.id}
                                role="button"
                                tabIndex={0}
                                onClick={() => setSelected(c)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault();
                                        setSelected(c);
                                    }
                                }}
                                data-testid={`class-item-${c.id}`}
                                className={
                                    "card p-4 cursor-pointer " +
                                    (selected?.id === c.id
                                        ? "border-[var(--cyan)] bg-[var(--cyan)]/5"
                                        : "")
                                }
                            >
                                <div className="font-display text-white text-lg leading-snug">
                                    {c.name}
                                </div>
                                <div className="flex items-center gap-3 mt-1">
                                    <span className="font-mono text-xs text-[var(--lime)] tracking-[0.22em]">
                                        {c.code}
                                    </span>
                                    <span className="font-mono text-xs text-[var(--muted)]">
                                        <Users size={11} className="inline mr-1" />
                                        {c.students_count}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </aside>

                    <section
                        className="col-span-12 md:col-span-8 card p-6"
                        data-testid="class-stats-panel"
                    >
                        {!selected ? null : statsLoading ? (
                            <p className="font-mono text-[var(--muted)]">…</p>
                        ) : (
                            stats && (
                                <>
                                    <div className="flex items-start justify-between flex-wrap gap-3">
                                        <div>
                                            <div className="font-display font-bold text-white text-2xl">
                                                {selected.name}
                                            </div>
                                            <div className="font-mono text-xs text-[var(--muted)] mt-1">
                                                {t.dashboard.code}:{" "}
                                                <span className="text-[var(--lime)] tracking-[0.18em]">
                                                    {selected.code}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <button
                                                onClick={() => copyCode(selected.code)}
                                                data-testid={`copy-code-${selected.id}`}
                                                className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--cyan)] hover:text-[var(--lime)] inline-flex items-center gap-1"
                                            >
                                                {copiedCode === selected.code ? (
                                                    <>
                                                        <CheckCheck size={14} />{" "}
                                                        {t.dashboard.codeCopied}
                                                    </>
                                                ) : (
                                                    <>
                                                        <Copy size={14} />{" "}
                                                        {t.dashboard.copyCode}
                                                    </>
                                                )}
                                            </button>
                                            <a
                                                href={classCsvUrl(selected.id)}
                                                data-testid={`export-csv-${selected.id}`}
                                                className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--lime)] hover:text-[var(--cyan)] inline-flex items-center gap-1"
                                                rel="noreferrer"
                                            >
                                                <FileDown size={14} /> {t.dashboard.exportCsv}
                                            </a>
                                            <button
                                                onClick={() => remove(selected.id)}
                                                data-testid={`delete-class-${selected.id}`}
                                                className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--coral)]"
                                            >
                                                <Trash2 size={14} className="inline mr-1" />
                                                {t.dashboard.del}
                                            </button>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-3 gap-3 mt-6">
                                        <Stat
                                            label={t.dashboard.students}
                                            value={stats.students_count}
                                            color="var(--cyan)"
                                            Icon={Users}
                                        />
                                        <Stat
                                            label={t.dashboard.completion}
                                            value={`${stats.completion_rate}%`}
                                            color="var(--lime)"
                                            Icon={BarChart3}
                                        />
                                        <Stat
                                            label={t.dashboard.code}
                                            value={selected.code}
                                            color="var(--coral)"
                                            small
                                        />
                                    </div>

                                    {stats.students_count === 0 ? (
                                        <p
                                            className="font-mono text-[var(--muted)] mt-6 border-l-2 border-[var(--cyan)] pl-4"
                                            data-testid="class-empty"
                                        >
                                            {t.dashboard.noStudents}
                                        </p>
                                    ) : (
                                        <>
                                            {/* Per-week */}
                                            <h3 className="font-display font-bold text-[var(--lime)] uppercase text-sm tracking-widest mt-8 mb-3">
                                                {t.dashboard.perWeek}
                                            </h3>
                                            <div className="grid grid-cols-6 gap-2">
                                                {Object.entries(stats.per_week).map(
                                                    ([wid, w]) => (
                                                        <div
                                                            key={wid}
                                                            className="border border-[var(--border)] p-3 text-center"
                                                            data-testid={`per-week-${wid}`}
                                                        >
                                                            <div className="font-brush text-[var(--coral)] text-2xl -rotate-3">
                                                                {wid.toUpperCase()}
                                                            </div>
                                                            <div className="font-mono text-sm text-[var(--lime)]">
                                                                {w.avg_score}/{w.total}
                                                            </div>
                                                            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                                                                {w.attempted}{" "}
                                                                {t.dashboard.done}
                                                            </div>
                                                        </div>
                                                    ),
                                                )}
                                            </div>

                                            {/* Class Tank Health — water readings shared by students */}
                                            {waterStats && waterStats.count > 0 && (
                                                <>
                                                    <h3 className="font-display font-bold text-[var(--cyan)] uppercase text-sm tracking-widest mt-8 mb-3 flex items-center gap-2">
                                                        <Droplet size={14} />
                                                        {lang === "es"
                                                            ? "Salud del Tanque de Clase"
                                                            : "Class Tank Health"}
                                                        <span className="text-[var(--muted)] text-[10px] font-normal">
                                                            · {waterStats.count}{" "}
                                                            {lang === "es"
                                                                ? "lecturas"
                                                                : "readings"}
                                                        </span>
                                                        <a
                                                            href={classWaterCsvUrl(selected.id)}
                                                            data-testid={`water-csv-${selected.id}`}
                                                            className="ml-auto font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--lime)] hover:text-[var(--cyan)] inline-flex items-center gap-1 normal-case"
                                                            rel="noreferrer"
                                                        >
                                                            <FileDown size={12} /> CSV
                                                        </a>
                                                    </h3>
                                                    <div
                                                        className="grid grid-cols-2 md:grid-cols-5 gap-2"
                                                        data-testid="class-water-stats"
                                                    >
                                                        {[
                                                            ["pH", waterStats.averages.ph, "var(--lime)"],
                                                            ["NH₃", waterStats.averages.ammonia, "var(--coral)"],
                                                            ["NO₂", waterStats.averages.nitrite, "var(--cyan)"],
                                                            ["NO₃", waterStats.averages.nitrate, "var(--lime)"],
                                                            ["°C", waterStats.averages.temp_c, "var(--cyan)"],
                                                        ].map(([k, v, c]) => (
                                                            <div
                                                                key={k}
                                                                className="border border-[var(--border)] p-3 text-center"
                                                            >
                                                                <div
                                                                    className="font-mono text-[10px] uppercase tracking-widest"
                                                                    style={{ color: c }}
                                                                >
                                                                    avg {k}
                                                                </div>
                                                                <div className="font-display font-black text-white text-xl mt-1">
                                                                    {v ?? "—"}
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {Object.keys(waterStats.flag_counts || {}).length > 0 && (
                                                        <div
                                                            className="flex items-center gap-2 flex-wrap mt-3"
                                                            data-testid="class-water-flags"
                                                        >
                                                            <AlertTriangle
                                                                size={12}
                                                                className="text-[var(--coral)]"
                                                            />
                                                            <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--coral)]">
                                                                {lang === "es"
                                                                    ? "Alertas en curso:"
                                                                    : "Active flags:"}
                                                            </span>
                                                            {Object.entries(waterStats.flag_counts).map(
                                                                ([f, n]) => (
                                                                    <span
                                                                        key={f}
                                                                        className="font-mono text-[10px] border border-[var(--coral)] text-[var(--coral)] px-2 py-0.5"
                                                                    >
                                                                        {f} × {n}
                                                                    </span>
                                                                ),
                                                            )}
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Roster (sorted as leaderboard by backend) */}
                                            <h3 className="font-display font-bold text-[var(--cyan)] uppercase text-sm tracking-widest mt-8 mb-3">
                                                {t.dashboard.roster}
                                            </h3>
                                            <div className="space-y-2">
                                                {stats.students.map((s) => {
                                                    const medal =
                                                        s.rank === 1
                                                            ? "🥇"
                                                            : s.rank === 2
                                                              ? "🥈"
                                                              : s.rank === 3
                                                                ? "🥉"
                                                                : null;
                                                    return (
                                                        <div
                                                            key={s.user_id}
                                                            className={
                                                                "border p-3 flex items-center justify-between flex-wrap gap-2 " +
                                                                (s.rank <= 3
                                                                    ? "border-[var(--lime)]/40 bg-[var(--lime)]/[0.03]"
                                                                    : "border-[var(--border)]")
                                                            }
                                                            data-testid={`roster-${s.user_id}`}
                                                        >
                                                            <div className="min-w-[180px] flex items-center gap-3">
                                                                <span
                                                                    className={
                                                                        "font-display font-black text-lg w-7 text-center " +
                                                                        (s.rank === 1
                                                                            ? "text-[var(--lime)]"
                                                                            : s.rank <= 3
                                                                              ? "text-[var(--cyan)]"
                                                                              : "text-[var(--muted)]")
                                                                    }
                                                                    data-testid={`rank-${s.user_id}`}
                                                                >
                                                                    {medal || `#${s.rank}`}
                                                                </span>
                                                                <div>
                                                                    <div className="font-mono text-white">
                                                                        {s.name || s.email}
                                                                    </div>
                                                                    <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] flex items-center gap-2 mt-1">
                                                                        <span>
                                                                            {s.completed_weeks}/6{" "}
                                                                            {t.dashboard.done}
                                                                        </span>
                                                                        <span>·</span>
                                                                        <span>
                                                                            {s.total_score} pts
                                                                        </span>
                                                                        <span>·</span>
                                                                        <span className="text-[var(--coral)] inline-flex items-center gap-1">
                                                                            <Flame size={10} />
                                                                            {s.streak}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <div className="flex gap-1">
                                                                {[
                                                                    "w1",
                                                                    "w2",
                                                                    "w3",
                                                                    "w4",
                                                                    "w5",
                                                                    "w6",
                                                                ].map((wid) => {
                                                                    const sc = s.progress[wid];
                                                                    return (
                                                                        <span
                                                                            key={wid}
                                                                            className={
                                                                                "font-mono text-[10px] w-7 h-7 grid place-items-center border " +
                                                                                (sc !== undefined
                                                                                    ? sc === 3
                                                                                        ? "border-[var(--lime)] text-[var(--lime)]"
                                                                                        : "border-[var(--cyan)] text-[var(--cyan)]"
                                                                                    : "border-[var(--border)] text-[var(--muted)]")
                                                                            }
                                                                            title={`${wid}: ${sc ?? "—"}`}
                                                                        >
                                                                            {sc !== undefined
                                                                                ? sc
                                                                                : "·"}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </>
                                    )}
                                </>
                            )
                        )}
                    </section>
                </div>
            )}
            <PaywallModal
                open={!!paywall}
                onClose={() => setPaywall(null)}
                feature={paywall?.feature}
            />
        </section>
    );
}

function Stat({ Icon, label, value, color, small }) {
    return (
        <div className="border border-[var(--border)] p-4">
            {Icon && <Icon size={14} style={{ color }} />}
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[var(--muted)] mt-1">
                {label}
            </div>
            <div
                className={
                    (small
                        ? "font-mono text-base mt-1"
                        : "font-display font-black text-2xl mt-1") + " text-white"
                }
                style={small ? { color } : {}}
            >
                {value}
            </div>
        </div>
    );
}
